import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { chatbotService, ChatbotManageConfig } from '../../services/chatbotService';
import ChatbotSubnav from './ChatbotSubnav';

const emptyFaq = (): { question: string; answer: string } => ({ question: '', answer: '' });

const ChatbotSettings: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<Partial<ChatbotManageConfig>>({
    enabled: false,
    business_name: '',
    business_summary: '',
    faqs: [],
    allowed_domains: [],
    welcome_message: '',
    notification_emails: [],
    primary_color: '#2563eb',
    widget_theme: 'light',
    launcher_position: 'bottom_right',
    auto_open_enabled: false,
    auto_open_delay_seconds: 5,
    auto_open_mode: 'panel',
    proactive_message: '',
  });
  const [domainsText, setDomainsText] = useState('');
  const [notifyText, setNotifyText] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const c = await chatbotService.getConfig();
      const doms = Array.isArray(c.allowed_domains) ? c.allowed_domains : [];
      const notify = Array.isArray(c.notification_emails) ? c.notification_emails : [];
      setDomainsText(doms.join('\n'));
      setNotifyText(notify.join('\n'));
      setForm({
        enabled: c.enabled,
        business_name: c.business_name || '',
        business_summary: c.business_summary || '',
        faqs: Array.isArray(c.faqs) ? c.faqs : [],
        allowed_domains: doms,
        welcome_message: c.welcome_message || '',
        notification_emails: notify,
        primary_color: c.primary_color || '#2563eb',
        widget_theme: c.widget_theme || 'light',
        launcher_position: c.launcher_position || 'bottom_right',
        auto_open_enabled: !!c.auto_open_enabled,
        auto_open_delay_seconds: c.auto_open_delay_seconds ?? 5,
        auto_open_mode: c.auto_open_mode || 'panel',
        proactive_message: c.proactive_message || '',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const faqs = (form.faqs || []) as { question?: string; answer?: string }[];

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    setError('');
    try {
      await chatbotService.patchConfig({
        enabled: form.enabled,
        business_name: form.business_name,
        business_summary: form.business_summary,
        faqs: faqs.filter((f) => (f.question || '').trim()),
        allowed_domains: domainsText
          .split(/[\n,]+/)
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
        welcome_message: form.welcome_message,
        notification_emails: notifyText
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        primary_color: form.primary_color,
        widget_theme: form.widget_theme,
        launcher_position: form.launcher_position,
        auto_open_enabled: form.auto_open_enabled,
        auto_open_delay_seconds: form.auto_open_delay_seconds,
        auto_open_mode: form.auto_open_mode,
        proactive_message: form.proactive_message,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <ChatbotSubnav />
        <p className="text-fg-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <ChatbotSubnav />
      <h1 className="text-2xl font-semibold text-fg mb-2">Chatbot settings</h1>
      <p className="text-sm text-fg-secondary mb-6">
        Configure your embedded assistant. Allowed domains must match the site where you install the widget (Origin check).
      </p>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-sm">
          {error}
        </div>
      )}
      {!isAdmin && (
        <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">
          Only admins can edit chatbot settings. You can view the Install tab for the embed code.
        </p>
      )}

      <div className="space-y-4 bg-surface border border-border rounded-lg p-6 shadow-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.enabled}
            disabled={!isAdmin}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          <span className="text-sm font-medium text-fg">Enable chatbot</span>
        </label>

        <div className="pt-4 border-t border-border space-y-4">
          <h2 className="text-sm font-semibold text-fg">Appearance</h2>
          <div>
            <label className="block text-sm font-medium text-fg mb-1">Accent color</label>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="color"
                value={(form.primary_color || '#2563eb').slice(0, 7)}
                disabled={!isAdmin}
                onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                className="h-10 w-14 cursor-pointer rounded border border-border"
              />
              <input
                type="text"
                className="flex-1 min-w-[120px] px-3 py-2 border border-border rounded-md bg-app text-fg font-mono text-sm"
                value={form.primary_color || '#2563eb'}
                disabled={!isAdmin}
                onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                placeholder="#2563eb"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-fg mb-1">Widget theme</label>
            <select
              className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
              value={form.widget_theme || 'light'}
              disabled={!isAdmin}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  widget_theme: e.target.value as ChatbotManageConfig['widget_theme'],
                }))
              }
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-fg mb-1">Launcher position</label>
            <select
              className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
              value={form.launcher_position || 'bottom_right'}
              disabled={!isAdmin}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  launcher_position: e.target.value as ChatbotManageConfig['launcher_position'],
                }))
              }
            >
              <option value="bottom_right">Bottom right</option>
              <option value="bottom_left">Bottom left</option>
            </select>
          </div>
        </div>

        <div className="pt-4 border-t border-border space-y-4">
          <h2 className="text-sm font-semibold text-fg">Proactive chat</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.auto_open_enabled}
              disabled={!isAdmin}
              onChange={(e) => setForm((f) => ({ ...f, auto_open_enabled: e.target.checked }))}
            />
            <span className="text-sm font-medium text-fg">Automatically engage visitors</span>
          </label>
          {form.auto_open_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-fg mb-1">Delay (seconds)</label>
                <input
                  type="number"
                  min={0}
                  max={120}
                  className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
                  value={form.auto_open_delay_seconds ?? 5}
                  disabled={!isAdmin}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      auto_open_delay_seconds: Math.min(
                        120,
                        Math.max(0, Number(e.target.value) || 0)
                      ),
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-fg mb-1">Engagement style</label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
                  value={form.auto_open_mode || 'panel'}
                  disabled={!isAdmin}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      auto_open_mode: e.target.value as ChatbotManageConfig['auto_open_mode'],
                    }))
                  }
                >
                  <option value="panel">Open chat panel</option>
                  <option value="teaser">Show teaser bubble</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-fg mb-1">
                  Proactive message (optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
                  value={form.proactive_message || ''}
                  disabled={!isAdmin}
                  onChange={(e) => setForm((f) => ({ ...f, proactive_message: e.target.value }))}
                  placeholder="Defaults to welcome message if empty"
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Business name</label>
          <input
            className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
            value={form.business_name || ''}
            disabled={!isAdmin}
            onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Business summary</label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg min-h-[100px]"
            value={form.business_summary || ''}
            disabled={!isAdmin}
            onChange={(e) => setForm((f) => ({ ...f, business_summary: e.target.value }))}
            placeholder="What you offer, who you serve (no pricing required)."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Welcome message</label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg"
            value={form.welcome_message || ''}
            disabled={!isAdmin}
            onChange={(e) => setForm((f) => ({ ...f, welcome_message: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">
            Allowed domains (one per line)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg font-mono text-sm"
            value={domainsText}
            disabled={!isAdmin}
            onChange={(e) => setDomainsText(e.target.value)}
            placeholder="example.com"
            rows={4}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">FAQs</label>
          <div className="space-y-3">
            {faqs.map((row, i) => (
              <div key={i} className="grid gap-2 md:grid-cols-2">
                <input
                  className="px-3 py-2 border border-border rounded-md bg-app text-fg text-sm"
                  placeholder="Question"
                  disabled={!isAdmin}
                  value={row.question || ''}
                  onChange={(e) => {
                    const next = [...faqs];
                    next[i] = { ...next[i], question: e.target.value };
                    setForm((f) => ({ ...f, faqs: next }));
                  }}
                />
                <input
                  className="px-3 py-2 border border-border rounded-md bg-app text-fg text-sm"
                  placeholder="Answer"
                  disabled={!isAdmin}
                  value={row.answer || ''}
                  onChange={(e) => {
                    const next = [...faqs];
                    next[i] = { ...next[i], answer: e.target.value };
                    setForm((f) => ({ ...f, faqs: next }));
                  }}
                />
              </div>
            ))}
            {isAdmin && (
              <button
                type="button"
                className="text-sm text-primary font-medium"
                onClick={() => setForm((f) => ({ ...f, faqs: [...faqs, emptyFaq()] }))}
              >
                + Add FAQ
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">
            Notification emails (optional, one per line; default: tenant admins)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md bg-app text-fg font-mono text-sm"
            value={notifyText}
            disabled={!isAdmin}
            onChange={(e) => setNotifyText(e.target.value)}
            rows={3}
          />
        </div>

        {isAdmin && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-md font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatbotSettings;
